import { useRef } from "react";
import { ExternalLink, Image, Play, MessageSquare, X } from "lucide-react";
import type { EmbedItem } from "../articleStatus";

const PROVIDER_LABELS: Record<string, string> = {
  twitter: "Twitter / X",
  youtube: "YouTube",
  vimeo: "Vimeo",
  instagram: "Instagram",
  tiktok: "TikTok",
  reddit: "Reddit",
  spotify: "Spotify",
  soundcloud: "SoundCloud",
};

function extractYouTubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
}

/** Lightbox dialog for inline images */
function ImageLightbox({
  src,
  alt,
  caption,
  credit,
}: {
  src: string;
  alt: string;
  caption?: string;
  credit?: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  return (
    <>
      <img
        src={src}
        alt={alt}
        className="max-h-64 w-full cursor-pointer object-contain transition-opacity hover:opacity-80"
        referrerPolicy="no-referrer"
        loading="lazy"
        onClick={() => dialogRef.current?.showModal()}
      />
      <dialog
        ref={dialogRef}
        className="fixed inset-0 z-50 m-0 h-full w-full max-h-full max-w-full bg-black/90 p-0 backdrop:bg-transparent"
        onClick={(e) => {
          if (e.target === e.currentTarget) dialogRef.current?.close();
        }}
      >
        <div className="flex h-full w-full flex-col items-center justify-center p-4">
          <button
            className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white hover:bg-white/40"
            onClick={() => dialogRef.current?.close()}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={src}
            alt={alt}
            className="max-h-[85vh] max-w-[95vw] object-contain"
            referrerPolicy="no-referrer"
          />
          {(caption || credit) && (
            <p className="ds-body-sm mt-3 max-w-2xl text-center text-white/80">
              {caption}
              {caption && credit && ". "}
              {credit && <span className="italic">{credit}</span>}
            </p>
          )}
        </div>
      </dialog>
    </>
  );
}

/** Small delete button shown in edit mode — top-right corner of the embed */
function DeleteButton({ onDelete }: { onDelete: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white transition-colors hover:bg-black/80"
      aria-label="Remove embed"
    >
      <X className="h-4 w-4" />
    </button>
  );
}

export function EmbedPreview({
  embed,
  editMode,
  onDelete,
}: {
  embed: EmbedItem;
  editMode?: boolean;
  onDelete?: () => void;
}) {
  const showDelete = editMode && !!onDelete;

  if (embed.type === "image") {
    return (
      <figure className="relative my-3 overflow-hidden rounded-lg border bg-muted/30">
        {showDelete && <DeleteButton onDelete={onDelete!} />}
        <ImageLightbox
          src={embed.wp_url || embed.url}
          alt={embed.alt || ""}
          caption={embed.caption}
          credit={embed.credit}
        />
        {(embed.caption || embed.credit) && (
          <figcaption className="ds-caption px-3 py-2 text-muted-foreground">
            {embed.caption}
            {embed.caption && embed.credit && ". "}
            {embed.credit && <span className="italic">{embed.credit}</span>}
          </figcaption>
        )}
      </figure>
    );
  }

  if (embed.type === "youtube") {
    const videoId = extractYouTubeId(embed.url);
    return (
      <div className="relative my-3 overflow-hidden rounded-lg border bg-muted/30">
        {showDelete && <DeleteButton onDelete={onDelete!} />}
        {videoId ? (
          <a href={embed.url} target="_blank" rel="noreferrer" className="group relative block">
            <img
              src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
              alt="YouTube video"
              className="aspect-video w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors group-hover:bg-black/40">
              <Play className="h-12 w-12 fill-white text-white drop-shadow-lg" />
            </div>
          </a>
        ) : (
          <EmbedLinkCard embed={embed} icon={<Play className="h-4 w-4" />} />
        )}
      </div>
    );
  }

  if (embed.type === "twitter") {
    return (
      <div className="relative my-3 rounded-lg border bg-muted/30 p-3">
        {showDelete && <DeleteButton onDelete={onDelete!} />}
        <div className="flex items-start gap-2">
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            {embed.fallback_text && (
              <p className="ds-body-sm mb-2 line-clamp-4">{embed.fallback_text}</p>
            )}
            <a
              href={embed.url}
              target="_blank"
              rel="noreferrer"
              className="ds-caption inline-flex items-center gap-1 text-primary hover:underline"
            >
              {PROVIDER_LABELS.twitter}
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (embed.type === "video") {
    return (
      <figure className="relative my-3 overflow-hidden rounded-lg border bg-muted/30">
        {showDelete && <DeleteButton onDelete={onDelete!} />}
        <video controls className="max-h-64 w-full" preload="metadata">
          <source src={embed.url} />
        </video>
      </figure>
    );
  }

  if (embed.type === "audio") {
    return (
      <figure className="relative my-3 overflow-hidden rounded-lg border bg-muted/30 p-3">
        {showDelete && <DeleteButton onDelete={onDelete!} />}
        <audio controls className="w-full" preload="metadata">
          <source src={embed.url} />
        </audio>
      </figure>
    );
  }

  // Generic embed card for instagram, tiktok, reddit, vimeo, etc.
  const icon =
    embed.type === "vimeo" ? <Play className="h-4 w-4" /> : <Image className="h-4 w-4" />;

  return (
    <div className="relative my-3">
      {showDelete && <DeleteButton onDelete={onDelete!} />}
      <EmbedLinkCard embed={embed} icon={icon} />
    </div>
  );
}

function EmbedLinkCard({ embed, icon }: { embed: EmbedItem; icon: React.ReactNode }) {
  const label = PROVIDER_LABELS[embed.provider || ""] || embed.provider || embed.type;
  return (
    <a
      href={embed.url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3 transition-colors hover:bg-muted/50"
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-muted-foreground">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="ds-body-sm-strong">{label}</p>
        <p className="ds-caption truncate text-muted-foreground">{embed.url}</p>
      </div>
      <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
    </a>
  );
}
